import { Injectable } from '@angular/core';
import { ShipmentFilterService } from '../services/shipment-filter.service';
import { FilterAssistQuery } from '../../../interfaces/filter-assist-query';
import { ShipmentQuery } from '../../../models/shipment-query';
import { FilterAssist } from '../../../models/filter-assist';
import { CountriesService } from '../services/countries.service';
import { Observable, of } from 'rxjs';
import { FilterCategoryEnum } from '../../../enum/filter-category.enum';

@Injectable({
    providedIn: 'root'
})
export class ShipmentCardFilterService implements FilterAssistQuery<ShipmentQuery> {

    private SHIPMENT_SHIPPER_ATTN = 'SHIPMENT_SHIPPER_ATTN';
    private SHIPMENT_SHIPPER_ORGNAME = 'SHIPMENT_SHIPPER_ORGNAME';
    private SHIPMENT_SHIPPER_CITY = 'SHIPMENT_SHIPPER_CITY';
    private SHIPMENT_SHIPPER_STREET1 = 'SHIPMENT_SHIPPER_STREET1';
    private SHIPMENT_SHIPPER_STREET2 = 'SHIPMENT_SHIPPER_STREET2';
    private SHIPMENT_SHIPPER_PROVINCE = 'SHIPMENT_SHIPPER_PROVINCE';
    private SHIPMENT_SHIPPER_COUNTRY = 'SHIPMENT_SHIPPER_COUNTRY';
    private SHIPMENT_SHIPPER_POSTALCODE = 'SHIPMENT_SHIPPER_POSTALCODE';
    private SHIPMENT_SHIPPER_EMAIL = 'SHIPMENT_SHIPPER_EMAIL';
    private SHIPMENT_SHIPPER_TEL = 'SHIPMENT_SHIPPER_TEL';

    private SHIPMENT_DESTINATION_ATTN = 'SHIPMENT_DESTINATION_ATTN';
    private SHIPMENT_DESTINATION_ORGNAME = 'SHIPMENT_DESTINATION_ORGNAME';
    private SHIPMENT_DESTINATION_CITY = 'SHIPMENT_DESTINATION_CITY';
    private SHIPMENT_DESTINATION_STREET1 = 'SHIPMENT_DESTINATION_STREET1';
    private SHIPMENT_DESTINATION_STREET2 = 'SHIPMENT_DESTINATION_STREET2';
    private SHIPMENT_DESTINATION_PROVINCE = 'SHIPMENT_DESTINATION_PROVINCE';
    private SHIPMENT_DESTINATION_COUNTRY = 'SHIPMENT_DESTINATION_COUNTRY';
    private SHIPMENT_DESTINATION_POSTALCODE = 'SHIPMENT_DESTINATION_POSTALCODE';
    private SHIPMENT_DESTINATION_EMAIL = 'SHIPMENT_DESTINATION_EMAIL';
    private SHIPMENT_DESTINATION_TEL = 'SHIPMENT_DESTINATION_TEL';

    constructor(private shipmentFilterService: ShipmentFilterService, private countriesService: CountriesService) {
        shipmentFilterService.registerFilters(this);
    }

    protected filterProperty(id: string): string {
        return id.toLowerCase().split('_')[2];
    }

    protected filterField(id: string): string {
        return id.toLowerCase().split('_')[1];
    }

    protected filterActive(field: string, property: string, query: ShipmentQuery): string {
        const list = query[field];
        for (const item of list) {
            if (item.property === property) {
                return item.value;
            }
        }
        return null;
    }

    active(query: ShipmentQuery): Observable<FilterAssist[]> {
        const list = [];
        for (const id of this.ids()) {
            const field = this.filterField(id);
            const property = this.filterProperty(id);
            const value = this.filterActive(field, property, query);
            if (property === 'country' && this.filterActive(field, 'province', query)) {
                continue;
            }
            if (value !== null) {
                if (property === 'province') {
                    const country = this.filterActive(field, 'country', query);
                    list.push({
                        id, description: this.description(id, value, country), parameter: [ value, country ], score: 0
                    });
                } else {
                    list.push({
                        id, description: this.description(id, value), parameter: value, score: 0
                    });
                }
            }
        }
        return of(list);
    }

    apply(id: string, parameter: any, query: ShipmentQuery): void {
        this.unapply(id, query);
        if (id === this.SHIPMENT_DESTINATION_COUNTRY) {
            this.unapply(this.SHIPMENT_DESTINATION_PROVINCE, query);
        }
        if (id === this.SHIPMENT_SHIPPER_COUNTRY) {
            this.unapply(this.SHIPMENT_SHIPPER_PROVINCE, query);
        }
        const property = this.filterProperty(id);
        const field = this.filterField(id);
        switch (field) {
            case 'shipper':
                if (property === 'province') {
                    query.shipper.push({
                        property, operator: 'eq', value: parameter[0]
                    });
                    query.shipper.push({
                        property: 'country', operator: 'eq', value: parameter[1]
                    });
                } else {
                    query.shipper.push({
                        property, operator: 'contains', value: parameter
                    });
                }
                break;
            case 'destination':
                if (property === 'province') {
                    query.destination.push({
                        property, operator: 'eq', value: parameter[0]
                    });
                    query.destination.push({
                        property: 'country', operator: 'eq', value: parameter[1]
                    });
                } else {
                    query.destination.push({
                        property, operator: 'contains', value: parameter
                    });
                }
                break;
        }
    }

    protected description(id: string, value: string, value2: string = null): string {
        let name;
        let country;
        switch (id) {
            case this.SHIPMENT_DESTINATION_COUNTRY:
                name = this.countriesService.countryName(value);
                return $localize`Shipping to ${name}`;
            case this.SHIPMENT_SHIPPER_COUNTRY:
                name = this.countriesService.countryName(value);
                return $localize`Shipping from ${name}`;
            case this.SHIPMENT_DESTINATION_PROVINCE:
                country = this.countriesService.countryName(value2);
                name = this.countriesService.provinceName(value2, value);
                return $localize`Shipping to ${name}, ${country}`;
            case this.SHIPMENT_SHIPPER_PROVINCE:
                country = this.countriesService.countryName(value2);
                name = this.countriesService.provinceName(value2, value);
                return $localize`Shipping from ${name}, ${country}`;
            case this.SHIPMENT_SHIPPER_ATTN:
                return $localize`Shipper name contains "${value}"`;
            case this.SHIPMENT_DESTINATION_ATTN:
                return $localize`Destination name contains "${value}"`;
            case this.SHIPMENT_SHIPPER_ORGNAME:
                return $localize`Shipper company contains "${value}"`;
            case this.SHIPMENT_DESTINATION_ORGNAME:
                return $localize`Destination company contains "${value}"`;
            case this.SHIPMENT_SHIPPER_CITY:
                return $localize`Shipper city contains "${value}"`;
            case this.SHIPMENT_DESTINATION_CITY:
                return $localize`Destination city contains "${value}"`;
            case this.SHIPMENT_SHIPPER_STREET1:
                return $localize`Shipper street #1 address contains "${value}"`;
            case this.SHIPMENT_DESTINATION_STREET1:
                return $localize`Destination street #1 address contains "${value}"`;
        }
        return `unrecognized filter ${id}`;
    }

    ids(): string[] {
        return [
            this.SHIPMENT_SHIPPER_ATTN,
            this.SHIPMENT_SHIPPER_ORGNAME,
            this.SHIPMENT_SHIPPER_CITY,
            this.SHIPMENT_SHIPPER_STREET1,
            this.SHIPMENT_SHIPPER_STREET2,
            this.SHIPMENT_SHIPPER_PROVINCE,
            this.SHIPMENT_SHIPPER_COUNTRY,
            this.SHIPMENT_SHIPPER_POSTALCODE,
            this.SHIPMENT_SHIPPER_EMAIL,
            this.SHIPMENT_SHIPPER_TEL,
            this.SHIPMENT_DESTINATION_ATTN,
            this.SHIPMENT_DESTINATION_ORGNAME,
            this.SHIPMENT_DESTINATION_CITY,
            this.SHIPMENT_DESTINATION_STREET1,
            this.SHIPMENT_DESTINATION_STREET2,
            this.SHIPMENT_DESTINATION_PROVINCE,
            this.SHIPMENT_DESTINATION_COUNTRY,
            this.SHIPMENT_DESTINATION_POSTALCODE,
            this.SHIPMENT_DESTINATION_EMAIL,
            this.SHIPMENT_DESTINATION_TEL,
        ];
    }

    match(terms: string): Observable<FilterAssist[]> {
        const output = [];
        if (terms.length < 2) {
            return of(output);
        }
        this.countriesService.countryFilter(terms).slice(0, 3).forEach((code) => {
            output.push({
                id: this.SHIPMENT_DESTINATION_COUNTRY,
                parameter: code,
                description: this.description(this.SHIPMENT_DESTINATION_COUNTRY, code),
                score: code.toUpperCase() === terms.toUpperCase() ? 10 : 50,
                category: FilterCategoryEnum.DESTINATION
            });
            output.push({
                id: this.SHIPMENT_SHIPPER_COUNTRY,
                parameter: code,
                description: this.description(this.SHIPMENT_SHIPPER_COUNTRY, code),
                score: code.toUpperCase() === terms.toUpperCase() ? 15 : 55,
                category: FilterCategoryEnum.SHIPPER
            });
        });
        this.countriesService.searchAllProvinces(terms).forEach((province) => {
            output.push({
                id: this.SHIPMENT_DESTINATION_PROVINCE,
                parameter: [ province.code, province.country ],
                description: this.description(this.SHIPMENT_DESTINATION_PROVINCE, province.code, province.country),
                score: 7,
                category: FilterCategoryEnum.DESTINATION
            });
            output.push({
                id: this.SHIPMENT_SHIPPER_PROVINCE,
                parameter: [ province.code, province.country ],
                description: this.description(this.SHIPMENT_SHIPPER_PROVINCE, province.code, province.country),
                score: 8,
                category: FilterCategoryEnum.SHIPPER
            });
        });
        if (terms.length > 2) {
            for (const id of [ this.SHIPMENT_SHIPPER_ATTN, this.SHIPMENT_SHIPPER_CITY, this.SHIPMENT_SHIPPER_STREET1,
                this.SHIPMENT_SHIPPER_ORGNAME ]) {
                output.push({
                    id,
                    parameter: terms,
                    description: this.description(id, terms),
                    score: 50,
                    category: FilterCategoryEnum.SHIPPER
                });
            }
            for (const id of [ this.SHIPMENT_DESTINATION_ATTN, this.SHIPMENT_DESTINATION_CITY,
                this.SHIPMENT_DESTINATION_STREET1, this.SHIPMENT_DESTINATION_ORGNAME ]) {
                output.push({
                    id,
                    parameter: terms,
                    description: this.description(id, terms),
                    score: 50,
                    category: FilterCategoryEnum.DESTINATION
                });
            }
        }
        return of(output);
    }

    unapply(id: string, query: ShipmentQuery): void {
        const property = this.filterProperty(id);
        const field = this.filterField(id);
        switch (field) {
            case 'shipper':
                query.shipper = query.shipper.filter((filter) => filter.property !== property);
                if (property === 'province') {
                    query.shipper = query.shipper.filter((filter) => filter.property !== 'country');
                }
                break;
            case 'destination':
                query.destination = query.destination.filter((filter) => filter.property !== property);
                if (property === 'province') {
                    query.destination = query.destination.filter((filter) => filter.property !== 'country');
                }
                break;
        }
    }


}
